import { useCallback, useEffect, useRef } from "react";
import { getMarketRatioTopic, socketConnection } from "@/lib/natsConnection";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export interface IMarketRatioData {
  min: number;
  value: number;
  max: number;
}

export interface IMarketRatio {
  symbol: string;
  timestamp: number;
  data: {
    "1M": IMarketRatioData;
    "1d": IMarketRatioData;
    "1w": IMarketRatioData;
  };
}

export type IMarketRatioAllData = Record<string, IMarketRatio>;

const BATCH_UPDATE_INTERVAL = 100; // ms
const key = ["marketRatioAllData"] as const;

export function useMarketRatioAll(symbols: string[]) {
  const unsubscribeRef = useRef<Record<string, () => void>>({});
  const batchedUpdatesRef = useRef<Record<string, IMarketRatio>>({});
  const batchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const oldSymbols = useRef<string[]>([]);
  const queryClient = useQueryClient();

  const processBatchedUpdates = useCallback(() => {
    if (Object.keys(batchedUpdatesRef.current).length === 0) {
      return;
    }

    queryClient.setQueryData<IMarketRatioAllData>(key, (currentData = {}) => {
      const updates = { ...batchedUpdatesRef.current };
      batchedUpdatesRef.current = {};

      // Only update changed values
      const newData = { ...currentData };
      let hasChanges = false;

      Object.entries(updates).forEach(([symbol, newValue]) => {
        const oldValue = currentData[symbol];
        if (
          !oldValue ||
          JSON.stringify(oldValue) !== JSON.stringify(newValue)
        ) {
          newData[symbol] = newValue;
          hasChanges = true;
        }
      });

      return hasChanges ? newData : currentData;
    });
  }, [queryClient]);

  const scheduleBatchUpdate = useCallback(() => {
    if (batchTimeoutRef.current) {
      return;
    }

    batchTimeoutRef.current = setTimeout(() => {
      processBatchedUpdates();
      batchTimeoutRef.current = null;
    }, BATCH_UPDATE_INTERVAL);
  }, [processBatchedUpdates]);

  const handleMarketRatioUpdate = useCallback(
    (newData: IMarketRatio) => {
      batchedUpdatesRef.current[newData.symbol] = newData;
      scheduleBatchUpdate();
    },
    [scheduleBatchUpdate],
  );

  useEffect(() => {
    const newSymbols = symbols.filter((s) => !oldSymbols.current.includes(s));
    const removedSymbols = oldSymbols.current.filter(
      (s) => !symbols.includes(s),
    );

    // Subscribe to new symbols
    newSymbols.forEach((symbol) => {
      const topic = getMarketRatioTopic(symbol);
      socketConnection
        .subscribe(topic, handleMarketRatioUpdate)
        .then((unsub) => {
          unsubscribeRef.current[symbol] = unsub;
        });
    });

    // Unsubscribe from removed symbols
    removedSymbols.forEach((symbol) => {
      if (unsubscribeRef.current[symbol]) {
        unsubscribeRef.current[symbol]();
        delete unsubscribeRef.current[symbol];

        // Remove data for unsubscribed symbol
        queryClient.setQueryData<IMarketRatioAllData>(
          key,
          (currentData = {}) => {
            const { [symbol]: removed, ...rest } = currentData;
            return rest;
          },
        );
      }
    });

    oldSymbols.current = symbols;

    // Cleanup batch timeout on symbols change
    return () => {
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
        batchTimeoutRef.current = null;
      }
    };
  }, [symbols, queryClient, handleMarketRatioUpdate]);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }

      Object.values(unsubscribeRef.current).forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, []);

  return useQuery<IMarketRatioAllData>({
    queryKey: key,
    initialData: {},
    enabled: false,
    staleTime: Infinity,
  });
}
